/**
 * 購物車組件
 * 顯示購物車內容並提供數量調整和刪除功能
 */

import { useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import { useCart } from '../hooks/useCart';
import { formatTwdFromCents, centsToYuan } from '@pos/shared/money';
import { getVariantDisplayText } from '@pos/shared/utils';
import { calculateCartTotalInCents } from '@pos/shared/lib/pricing';
import { useTenant } from '../hooks/queries/useTenant';
import useSWR from 'swr';
import { fetcher } from '../lib/fetcher';
import { useProducts } from '../hooks/queries';

/**
 * 購物車 Props
 */
export interface CartProps {
  onCheckout?: () => void;
  className?: string;
}

/**
 * 購物車組件
 */
export function Cart({ onCheckout, className = '' }: CartProps) {
  const { state, updateQuantity, removeItem } = useCart();

  // 取得 Authorization header（提供給 SDK fetch 選項使用）
  const getAuthFetchOptions = (): RequestInit | undefined => {
    const staffToken = localStorage.getItem('staff_session_token');
    const tenantToken = localStorage.getItem('tenant_token');
    const tenantId = localStorage.getItem('tenant_id');

    const token = staffToken || tenantToken;

    if (!token || !tenantId) return undefined;
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': tenantId,
      },
    };
  };

  // 取得 tenant 資訊（用於取得稅率）
  const { tenant } = useTenant({
    fetch: getAuthFetchOptions(),
    enabled: !!getAuthFetchOptions(),
    revalidateOnFocus: false,
    dedupingInterval: 300000, // 5 分鐘
  });

  // 從 tenant API 取得稅率（basis points，預設 500 = 5%）
  const taxRateBps = (tenant as any)?.tax_rate_bps ?? 500;

  // 計算折扣
  // 使用 items 的序列化作為 key 的一部分，確保數量變更時會重新請求
  const itemsKey =
    state.items.length > 0
      ? JSON.stringify(
          state.items.map(item => ({
            product_id: String(item.productId ?? item.id),
            quantity: item.quantity,
            price: item.price,
          }))
        )
      : null;

  const discountCalculationKey =
    state.items.length > 0 && itemsKey ? `discount-cart-${itemsKey}` : null;

  const discountCalculationUrl =
    state.items.length > 0
      ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'}/api/discounts/calculate-cart`
      : null;

  const { data: discountData } = useSWR<{
    data?: {
      total_discount?: number;
    };
  }>(
    discountCalculationKey,
    () =>
      fetcher(discountCalculationUrl!, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthFetchOptions()?.headers as HeadersInit),
        },
        body: JSON.stringify({
          items: state.items.map(item => ({
            product_id: String(item.productId ?? item.id),
            quantity: item.quantity,
            price: item.price,
          })),
        }),
      }),
    {
      revalidateOnFocus: true,
      dedupingInterval: 0, // 移除去重間隔，確保即時更新
      keepPreviousData: true, // 保持上一次的數據，避免在重新請求時閃爍（比照 tax 的處理方式）
    }
  );

  // 使用 useMemo 保持上一次的 discount 值，避免在重新請求時閃爍
  // 比照 tax 的處理方式：tax 是即時計算的，所以不會閃爍
  // discount 需要從 API 獲取，所以使用 keepPreviousData 和 useMemo 來保持穩定性
  const totalDiscount = useMemo(() => {
    return discountData?.data?.total_discount ?? 0;
  }, [discountData?.data?.total_discount]);

  // 全程整數「分」運算，稅金四捨五入到元，使顯示與應付一致（避免找零差 1）
  const cartTotal = calculateCartTotalInCents(
    state.totalAmount,
    totalDiscount,
    taxRateBps
  );
  const { subtotalCents, discountCents, taxDisplayCents, totalPayableCents } =
    cartTotal;

  // 查詢商品列表（用於獲取商品分類，計算點數）
  const { products: allProducts } = useProducts(
    {
      page: 1,
      limit: 100,
      is_active: 1,
    },
    {
      fetch: getAuthFetchOptions(),
      swr: {
        revalidateOnFocus: false,
        dedupingInterval: 300000, // 5 分鐘
      },
    }
  );

  // 查詢點數規則列表（用於計算預計獲得點數）
  const pointsRulesUrl = getAuthFetchOptions()
    ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'}/api/points-rules?page=1&limit=100&filter_is_active=1`
    : null;

  const { data: pointsRulesData } = useSWR<{
    data?: {
      rules?: Array<{
        id: string;
        points_per_yuan: number;
        target_type: 'ALL' | 'CATEGORY' | 'PRODUCT' | 'PRODUCTS';
        target_ids: string[];
        start_date: string;
        end_date: string;
      }>;
    };
  }>(pointsRulesUrl, url => fetcher(url, getAuthFetchOptions()), {
    revalidateOnFocus: true,
    dedupingInterval: 10000, // 10 秒內不重複請求
  });

  // 獲取商品的點數規則（用於計算預計獲得點數）
  const getPointsRuleForProduct = useMemo(() => {
    return (product: any, pointsRules: any[]): number | null => {
      const now = new Date();
      let bestRule: any = null;
      let bestPointsPerYuan = 0;

      for (const rule of pointsRules) {
        // 檢查規則是否在有效期內
        const startDate = new Date(rule.start_date);
        const endDate = new Date(rule.end_date);
        if (now < startDate || now > endDate) continue;

        // 檢查規則是否適用於此商品
        if (rule.target_type === 'ALL') {
          if (rule.points_per_yuan > bestPointsPerYuan) {
            bestRule = rule;
            bestPointsPerYuan = rule.points_per_yuan;
          }
        } else if (rule.target_type === 'CATEGORY') {
          const productCategory =
            typeof product.category === 'string' ? product.category : '';
          if (
            rule.target_ids.includes(productCategory) &&
            rule.points_per_yuan > bestPointsPerYuan
          ) {
            bestRule = rule;
            bestPointsPerYuan = rule.points_per_yuan;
          }
        } else if (
          rule.target_type === 'PRODUCT' ||
          rule.target_type === 'PRODUCTS'
        ) {
          const productId = String(product.id);
          if (
            rule.target_ids.includes(productId) &&
            rule.points_per_yuan > bestPointsPerYuan
          ) {
            bestRule = rule;
            bestPointsPerYuan = rule.points_per_yuan;
          }
        }
      }

      return bestRule ? bestPointsPerYuan : null;
    };
  }, []);

  // 計算預計獲得點數（一律計算並顯示，但只有會員才會實際存入）
  // 注意：只有當商品有對應的點數規則時才會計算點數
  const calculatePointsEarned = useMemo(() => {
    // 獲取點數規則並計算每個商品的點數
    const pointsRules = pointsRulesData?.data?.rules || [];

    let totalPoints = 0;

    state.items.forEach(item => {
      // 獲取商品的點數規則
      const productInfo = allProducts?.find(
        (p: any) => String(p.id) === String(item.id)
      );
      const productCategory = (productInfo as any)?.category || '';
      const pointsPerYuan = getPointsRuleForProduct(
        { id: item.id, category: productCategory },
        pointsRules
      );

      // 只有當商品有對應的點數規則時才計算點數
      if (pointsPerYuan !== null && pointsPerYuan > 0) {
        // SSOT：與 ConfirmDialog 一致。item.price 為分，先轉元再乘倍率（點數基於原價，不受折扣影響）
        const itemOriginalTotalYuan = centsToYuan(item.price * item.quantity);
        const itemPoints = Math.floor(itemOriginalTotalYuan * pointsPerYuan);
        totalPoints += itemPoints;
      }
    });

    return totalPoints;
  }, [state.items, pointsRulesData, allProducts, getPointsRuleForProduct]);

  /**
   * 處理數量變更
   */
  const handleQuantityChange = (id: number | string, newQuantity: number) => {
    updateQuantity(id, newQuantity);
  };

  /**
   * 處理刪除商品
   */
  const handleRemoveItem = (id: number | string) => {
    removeItem(id);
  };

  return (
    <div
      className={`bg-white rounded-2xl shadow-lg overflow-hidden ${className}`}
    >
      {/* 購物車標題 */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            購物車 ({state.itemCount} 項)
          </h2>
          <button className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* 購物車內容 */}
      <div className="p-4">
        {state.items.length === 0 ? (
          <div className="text-center py-8">
            <svg
              className="w-16 h-16 text-gray-300 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5 6m0 0h9.5"
              />
            </svg>
            <p className="text-gray-500">購物車是空的</p>
            <p className="text-sm text-gray-400">請選擇商品加入購物車</p>
          </div>
        ) : (
          <>
            {/* 商品列表 */}
            <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
              {state.items.map(item => {
                const variantText = getVariantDisplayText(item.variantLabel);
                return (
                  <div
                    key={item.id}
                    className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    {/* 商品圖片 */}
                    <div className="w-14 h-14 bg-white rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden shadow-sm">
                      {item.thumbnail ? (
                        <img
                          src={item.thumbnail}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <svg
                          className="w-7 h-7 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>

                    {/* 商品資訊 */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 truncate mb-1">
                        {item.name}
                        {variantText ? (
                          <span className="font-normal text-gray-500">
                            {' · '}
                            {variantText}
                          </span>
                        ) : null}
                      </h4>
                      <p className="text-sm font-medium text-blue-600">
                        {formatTwdFromCents(item.price)}
                      </p>
                    </div>

                    {/* 數量控制 */}
                    <div className="flex items-center space-x-2 bg-white rounded-lg p-1 border border-gray-200">
                      <button
                        onClick={() =>
                          handleQuantityChange(item.id, item.quantity - 1)
                        }
                        className="w-7 h-7 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md flex items-center justify-center transition-all duration-200 ease-out active:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={item.quantity <= 1}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M20 12H4"
                          />
                        </svg>
                      </button>

                      <span className="w-8 text-center text-sm font-semibold text-gray-900">
                        {item.quantity}
                      </span>

                      <button
                        onClick={() =>
                          handleQuantityChange(item.id, item.quantity + 1)
                        }
                        className="w-7 h-7 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md flex items-center justify-center transition-all duration-200 ease-out active:opacity-70"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* 小計和刪除按鈕 */}
                    <div className="flex flex-col items-end space-y-1">
                      <span className="text-sm font-bold text-gray-900">
                        {formatTwdFromCents(item.price * item.quantity)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(item.id)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                        title="刪除商品"
                        aria-label="刪除商品"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 總計 */}
            <div className="border-t border-gray-100 pt-5 mt-4">
              <div className="space-y-2 mb-5">
                <div className="flex justify-between items-center text-gray-700">
                  <span className="text-sm">小計</span>
                  <span className="text-sm font-medium">
                    {formatTwdFromCents(subtotalCents)}
                  </span>
                </div>
                {discountCents > 0 && (
                  <div className="flex justify-between items-center text-gray-700">
                    <span className="text-sm">Discount</span>
                    <span className="text-sm font-medium text-red-600">
                      -{formatTwdFromCents(discountCents)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center text-gray-700">
                  <span className="text-sm">
                    {taxRateBps > 0 ? `稅金 (${taxRateBps / 100}%)` : '稅金'}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatTwdFromCents(taxDisplayCents)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-100">
                <span className="text-lg font-bold text-gray-900">總計</span>
                <span className="text-xl font-bold text-green-600">
                  {formatTwdFromCents(totalPayableCents)}
                </span>
              </div>
              {/* 預計獲得點數（一律顯示，但只有會員才會實際存入） */}
              {calculatePointsEarned > 0 && (
                <div className="flex justify-between items-center mb-5">
                  <span className="text-sm text-gray-600">預計獲得點數</span>
                  <span className="text-sm font-medium text-blue-600">
                    {calculatePointsEarned} 點
                  </span>
                </div>
              )}

              {/* 結帳按鈕 */}
              <button
                onClick={onCheckout}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3.5 px-4 rounded-xl transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed active:opacity-70 shadow-md hover:shadow-lg"
                disabled={state.items.length === 0}
              >
                結帳
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
